import { useEffect, useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { Button, Grid, useMediaQuery } from "@mui/material";
import { Shuffle as ShuffleIcon, Save as SaveIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

import Layout from "../../components/Layout";
import DatePicker from "../../components/DatePicker";
import Alert from "../../components/Alert";
import Loader from "../../common/loader/Loader";
import Mobile from "./Mobile";
import Desktop from "./Desktop";

import useRandomWeek from "../../common/apollo/hooks/useRandomWeek";
import moment from "moment";

const styles = {
  actionButton: {
    "& .text": {
      display: { xs: "none", sm: "none", md: "inline" },
    },
  },
};

const SAVE_RANDOMIZE = gql`
  mutation saveRandomize($plan: [RandomizeInput]) {
    saveRandomize(plan: $plan)
  }
`;

const RandomPlan = () => {
  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);

  const {
    data: { id: planId, plan: data } = {},
    loading,
    error,
    refetch,
  } = useRandomWeek();
  const [saveRandomizeMutation, { loading: saveLoading, error: saveError }] =
    useMutation(SAVE_RANDOMIZE);

  const dateChanged = (start, end) => {
    setStart(start);
    setEnd(end);
  };

  useEffect(() => {
    if (!start) {
      setStart(moment().isoWeekday(1).startOf("day"));
    }
    if (!end) {
      setEnd(moment().isoWeekday(7).endOf("day"));
    }
    if (error || saveError) {
      setAlertMessage((error || saveError).message);
      setAlertSeverity("error");
      setAlertOpen(true);
    }
  }, [start, end, error, saveError]);

  let selectedWeek = null;
  if (data) {
    selectedWeek = moment(data[0].date).isoWeek();
  }

  const savePlan = () => {
    let itemDate,
      d = start.clone();
    let prevDate = null;
    const fmt = "YYYY-MM-DD";
    const body = data.map((item) => {
      itemDate = moment(item.date);
      if (prevDate && prevDate.format(fmt) !== itemDate.format(fmt)) {
        d = d.add(1, "day");
      }
      prevDate = itemDate;

      d.hour(itemDate.hour());
      if (item.amount) item.amount = parseFloat(item.amount);

      return {
        date: d.format("YYYY-MM-DD HH:mm:ss"),
        meal: item.meal,
        amount: item.amount,
        unit: item.unit?.name,
        dish: item.dish.id,
      };
    });

    saveRandomizeMutation({ variables: { plan: body } })
      .then(() => {
        setAlertMessage("Sačuvano.");
        setAlertSeverity("success");
        setAlertOpen(true);
      })
      .catch((err) => {
        setAlertMessage(err.message);
        setAlertSeverity("error");
        setAlertOpen(true);
      });
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const props = { start, end, planId, data, week: selectedWeek };

  return (
    <Layout title="Randomize">
      <Alert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        severity={alertSeverity}
        message={alertMessage}
        anchor={{ vertical: "top", horizontal: "center" }}
      />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <DatePicker
            variant="week"
            onChange={dateChanged}
            label="Odaberite nedelju"
            date={start}
            format="DD/MM/YYYY"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Button
            onClick={() => refetch()}
            color="secondary"
            variant="contained"
            startIcon={<ShuffleIcon fontSize="small" />}
            sx={styles.actionButton}
            fullWidth
          >
            <span className="text">Randomizuj</span>
          </Button>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Button
            onClick={savePlan}
            color="primary"
            variant="contained"
            disabled={data?.length === 0}
            startIcon={<SaveIcon fontSize="small" />}
            sx={styles.actionButton}
            fullWidth
          >
            <span className="text">Sačuvaj</span>
          </Button>
        </Grid>
        <Grid item xs={12}>
          {(loading || saveLoading) && <Loader />}
          {!loading && !saveLoading && !error && (
            <>
              {isMobile && <Mobile {...props} />}
              {!isMobile && <Desktop {...props} />}
            </>
          )}
        </Grid>
      </Grid>
    </Layout>
  );
};

export default RandomPlan;
